import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  Observable,
  ServerError,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode, print, type DocumentNode } from 'graphql';
import { createClient } from 'graphql-sse';
import { getGraphqlEndpoint, getSubscriptionsEndpoint } from '@/shared/lib/graphql-endpoint';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { storeRedirectTarget } from '@/app/auth/lib/redirect';

const getAccessToken = (): string | undefined => useAuthStore.getState().session?.access_token;

const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();

  if (token) {
    operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    }));
  }

  return forward(operation);
});

const errorLink = new ErrorLink(({ error }) => {
  const { session, clearSession } = useAuthStore.getState();
  if (!session) {
    return;
  }

  const unauthorized = ServerError.is(error)
    ? error.statusCode === 401
    : CombinedGraphQLErrors.is(error) &&
      error.errors.some((gqlError) => {
        const ext = gqlError.extensions as
          | { response?: { status?: unknown }; statusCode?: unknown }
          | undefined;
        const status = ext?.response?.status ?? ext?.statusCode;

        return status === 401;
      });

  if (unauthorized) {
    storeRedirectTarget(window.location.pathname + window.location.search);
    clearSession();
    window.location.assign('/login');
  }
});

let activeSseClient: ReturnType<typeof createClient> | undefined;

const isStreamUnauthorized = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err);
  return /\b401\b/.test(message);
};

const buildSseLink = () => {
  const subscriptionsEndpoint = getSubscriptionsEndpoint();
  if (!subscriptionsEndpoint) {
    return undefined;
  }

  activeSseClient?.dispose();
  const sseClient = createClient({
    url: subscriptionsEndpoint,
    headers: () => {
      const token = getAccessToken();
      return token ? { authorization: `Bearer ${token}` } : {};
    },
  });
  activeSseClient = sseClient;

  return new ApolloLink((operation) =>
    new Observable((observer) => {
      let iterator: AsyncIterator<unknown> | undefined;
      void (async () => {
        const it = sseClient.iterate({
          query: print(operation.query),
          variables: operation.variables,
          ...(operation.operationName ? { operationName: operation.operationName } : {}),
        });
        iterator = it;
        for await (const result of it) {
          observer.next(result as ApolloLink.Result);
        }
        observer.complete();
      })().catch((err: unknown) => {
        if (isStreamUnauthorized(err)) {
          storeRedirectTarget(window.location.pathname + window.location.search);
          useAuthStore.getState().clearSession();
          window.location.assign('/login');
          return;
        }
        observer.error(err);
      });
      return () => {
        void iterator?.return?.();
      };
    }),
  );
};

const isSubscription = ({ query }: { query: DocumentNode }) => {
  const definition = getMainDefinition(query);
  return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;
};

const buildLink = () => {
  const httpLink = authLink.concat(new HttpLink({ uri: getGraphqlEndpoint() }));
  const sseLink = buildSseLink();
  return errorLink.concat(sseLink ? ApolloLink.split(isSubscription, sseLink, httpLink) : httpLink);
};

export const repointApolloLink = (): void => {
  apolloClient.setLink(buildLink());
};

const listFieldPolicy = <T>() => ({
  keyArgs: false as const,
  merge(_existing: readonly T[] | undefined, incoming: readonly T[]) {
    return incoming;
  },
});

export const apolloClient = new ApolloClient({
  link: buildLink(),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listDevices: listFieldPolicy(),
          listDeviceGatewayAssignments: listFieldPolicy(),
          listDeviceRssi: listFieldPolicy(),
        },
      },
      Device: {
        fields: {
          meta: {
            merge(
              existing: Record<string, unknown> | undefined,
              incoming: Record<string, unknown>,
            ) {
              return {
                ...existing,
                ...incoming,
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
